import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./Reveal";
import Scene3D from "./Scene3D";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const sectionRef = useRef(null);
  const scrollRef = useRef(0);
  const concept =
    new URLSearchParams(window.location.search).get("concept") || "knot";

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      scrollRef.current = v;
    });
    return () => unsub();
  }, [scrollYProgress]);

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[80vh] w-[80vh] glow-radial opacity-80" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[50vh] w-[50vh] glow-radial opacity-40" />

      {/* 3D canvas layer */}
      <div className="absolute inset-0 z-10">
        <Scene3D scrollRef={scrollRef} variant={concept} />
      </div>

      {/* Text layer */}
      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-20 mx-auto max-w-[1500px] px-6 md:px-12 min-h-[100svh] flex flex-col justify-center pointer-events-none"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#C77DFF]" />
          <span className="text-xs md:text-sm uppercase tracking-[0.35em] text-[#C77DFF]">
            Creative Design Studio
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[0.92] tracking-tight text-white text-5xl sm:text-6xl lg:text-[7.5rem]">
          <MaskLine delay={0.35}>Bold ideas,</MaskLine>
          <MaskLine delay={0.5} className="text-outline">
            engineered
          </MaskLine>
          <MaskLine delay={0.65}>into design.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#B5A6CC] leading-relaxed"
        >
          CMD+DesignLab crafts strategic visual solutions — logos, campaigns,
          print and custom creative — for businesses, organizations and events
          that refuse to blend in.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4 pointer-events-auto"
        >
          <a
            href="#contact"
            data-testid="hero-cta-start"
            className="rounded-full bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white font-medium px-8 py-4 transition-colors duration-300"
          >
            Start a Project
          </a>
          <a
            href="#work"
            data-testid="hero-cta-work"
            className="rounded-full border border-[#3a2166] hover:border-[#C77DFF] text-white font-medium px-8 py-4 transition-colors duration-300"
          >
            View Our Work
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-[#B5A6CC]"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
